import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { cn } from '@/utils/cn';
import type { Tour } from './types';
import { resolveTarget } from './tourTarget';

const PAD = 8; // respiro ao redor do elemento destacado
const GAP = 12; // distância entre o elemento e o tooltip
const TIP_W = 340;
const MARGIN = 12; // margem mínima da borda da janela

interface Rect { top: number; left: number; width: number; height: number; }

export function TourOverlay({ tour, onClose }: { tour: Tour; onClose: () => void }) {
  const [step, setStep] = useState(0);
  const [rect, setRect] = useState<Rect | null>(null);
  const tipRef = useRef<HTMLDivElement>(null);
  const [tipPos, setTipPos] = useState<{ top: number; left: number } | null>(null);

  const total = tour.steps.length;
  const current = tour.steps[step];
  const isLast = step === total - 1;

  const next = useCallback(() => (isLast ? onClose() : setStep((s) => s + 1)), [isLast, onClose]);
  const prev = useCallback(() => setStep((s) => Math.max(0, s - 1)), []);

  // Mede o elemento-alvo do passo atual (e re-mede em scroll/resize).
  useLayoutEffect(() => {
    const measure = () => {
      const el = resolveTarget(current.target);
      if (!el) { setRect(null); return; }
      const r = el.getBoundingClientRect();
      setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
    };
    // garante que o alvo esteja visível antes de medir
    const el = resolveTarget(current.target);
    if (el) el.scrollIntoView({ block: 'center', behavior: 'smooth' });

    measure();
    // rAF LIMITADO no tempo: acompanha a animação do scrollIntoView (~600ms)
    // e então para — evita loop perpétuo que trava a página/CPU.
    const startedAt = performance.now();
    let raf = requestAnimationFrame(function tick(now) {
      measure();
      if (now - startedAt < 650) raf = requestAnimationFrame(tick);
    });
    // listeners cobrem scroll/resize do usuário depois disso
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
    };
  }, [current.target, step]);

  // Posiciona o tooltip relativo ao elemento (ou centraliza se não há alvo).
  useLayoutEffect(() => {
    const tipH = tipRef.current?.offsetHeight ?? 180;
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    if (!rect) {
      setTipPos({ top: Math.max(MARGIN, vh / 2 - tipH / 2), left: Math.max(MARGIN, vw / 2 - TIP_W / 2) });
      return;
    }

    const placement = current.placement ?? 'auto';
    const spaceBelow = vh - (rect.top + rect.height);
    const spaceAbove = rect.top;
    const spaceRight = vw - (rect.left + rect.width);

    let place = placement;
    if (placement === 'auto') {
      if (spaceBelow > tipH + GAP + MARGIN) place = 'bottom';
      else if (spaceAbove > tipH + GAP + MARGIN) place = 'top';
      else if (spaceRight > TIP_W + GAP + MARGIN) place = 'right';
      else place = 'left';
    }

    let top = 0, left = 0;
    switch (place) {
      case 'top': top = rect.top - tipH - GAP; left = rect.left + rect.width / 2 - TIP_W / 2; break;
      case 'bottom': top = rect.top + rect.height + GAP; left = rect.left + rect.width / 2 - TIP_W / 2; break;
      case 'left': left = rect.left - TIP_W - GAP; top = rect.top + rect.height / 2 - tipH / 2; break;
      case 'right': default: left = rect.left + rect.width + GAP; top = rect.top + rect.height / 2 - tipH / 2; break;
    }
    // mantém dentro da janela
    left = Math.min(Math.max(MARGIN, left), vw - TIP_W - MARGIN);
    top = Math.min(Math.max(MARGIN, top), vh - tipH - MARGIN);
    setTipPos({ top, left });
  }, [rect, current.placement, step]);

  // Teclado: Esc fecha, setas navegam.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowRight') next();
      else if (e.key === 'ArrowLeft') prev();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, next, prev]);

  return (
    <div className="fixed inset-0 z-[100]" role="dialog" aria-modal="true">
      {/* backdrop p/ capturar cliques (não fecha por acidente) */}
      <div className="absolute inset-0" />

      {/* spotlight: o box-shadow gigante escurece tudo, menos o recorte */}
      {rect && (
        <div
          className="pointer-events-none absolute rounded-lg ring-2 ring-brand-400 transition-all duration-200"
          style={{
            top: rect.top - PAD,
            left: rect.left - PAD,
            width: rect.width + PAD * 2,
            height: rect.height + PAD * 2,
            boxShadow: '0 0 0 9999px rgba(15, 23, 42, 0.55)',
          }}
        />
      )}
      {/* sem alvo: escurece a tela inteira */}
      {!rect && <div className="absolute inset-0 bg-slate-900/55" />}

      {/* tooltip */}
      <div
        ref={tipRef}
        className="absolute rounded-xl border border-border bg-card p-4 shadow-2xl transition-all duration-200"
        style={{ width: TIP_W, top: tipPos?.top ?? -9999, left: tipPos?.left ?? -9999 }}
      >
        <button
          onClick={onClose}
          className="absolute right-3 top-3 text-muted-foreground hover:text-foreground"
          aria-label="Fechar tour"
        >
          <X className="h-4 w-4" />
        </button>

        <h3 className="pr-6 text-base font-semibold text-foreground">{current.title}</h3>
        <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">{current.body}</p>

        <div className="mt-4 flex items-center justify-between gap-2">
          {/* progresso */}
          <div className="flex items-center gap-1.5">
            {tour.steps.map((_, i) => (
              <span
                key={i}
                className={cn(
                  'h-1.5 rounded-full transition-all',
                  i === step ? 'w-5 bg-brand-600' : 'w-1.5 bg-muted',
                )}
              />
            ))}
          </div>

          <div className="flex items-center gap-2">
            {step > 0 && (
              <Button variant="ghost" size="sm" onClick={prev} className="px-2">
                <ChevronLeft className="h-4 w-4" /> Voltar
              </Button>
            )}
            <Button size="sm" onClick={next}>
              {isLast ? 'Concluir' : (<>Próximo <ChevronRight className="h-4 w-4" /></>)}
            </Button>
          </div>
        </div>

        {!isLast && (
          <button
            onClick={onClose}
            className="mt-2 w-full text-center text-xs text-muted-foreground hover:text-foreground"
          >
            Pular tour
          </button>
        )}
      </div>
    </div>
  );
}
